import { createRoot } from 'react-dom/client'
import App, { type AppProps } from './components/App'
import { StoreProvider } from './store'
import type { IndexName, WidgetConfig } from './types'
import { WP_LOCALE } from '../../../assets/utils/algolia/algoliaLocalization'
import { history } from 'instantsearch.js/es/lib/routers'
import type { UiState } from 'instantsearch.js'

function getRouting(indexName: IndexName): AppProps<{
  q?: string
  status?: string
  lang?: string
  country?: string
}>['routing'] {
  return {
    router: history({
      cleanUrlOnDispose: false,
      parseURL({ qsModule, location }) {
        return qsModule.parse(location.search.slice(1))
      },
      createURL({ qsModule, location, routeState }) {
        const { origin, pathname, hash } = location
        const queriesFromUrl = qsModule.parse(location.search.slice(1))

        // Get all parameters from the URL that are not in the InstantSearch state
        const restQueries = Object.fromEntries(
          Object.entries(queriesFromUrl).filter(
            ([key]) => !Object.keys(routeState).includes(key)
          )
        )

        // Create query string with InstantSearch state and other parameters
        const queryString = qsModule.stringify(
          { ...routeState, ...restQueries },
          { addQueryPrefix: true, arrayFormat: 'repeat' }
        )

        return `${origin}${pathname}${queryString}${hash}`
      }
    }),
    stateMapping: {
      stateToRoute(uiState) {
        const indexState = uiState[indexName]
        return {
          q: indexState?.query,
          status: indexState?.menu?.status,
          lang: indexState?.menu?.['languages.name'],
          country: indexState?.menu?.[`country.name.${WP_LOCALE}`]
        }
      },
      routeToState(routeState) {
        return {
          [indexName]: {
            query: routeState.q,
            menu: {
              status: routeState.status,
              'languages.name': routeState.lang,
              [`country.name.${WP_LOCALE}`]: routeState.country
            }
          }
        } as UiState
      }
    }
  }
}

for (const container of document.querySelectorAll<HTMLElement>(
  '[data-location-finder-config]'
)) {
  const configJSON = container.dataset.locationFinderConfig
  if (!configJSON) {
    console.warn('Missing data-location-finder-config attribute')
    continue
  }
  const config = JSON.parse(configJSON) as WidgetConfig
  const routing = getRouting(config.indexName)

  const reactRoot = createRoot(container)
  reactRoot.render(
    <StoreProvider config={config} routing={true}>
      <App routing={routing} />
    </StoreProvider>
  )
}
